import React, { useState, useEffect, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card"
import { Badge } from "./ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog"
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"

import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarNav,
  SidebarNavItem,
  SidebarFooter,
} from "./ui/sidebar"

import {
  Sun,
  Cloud,
  CloudRain,
  CloudSnow,
  Wind,
  Droplets,
  Thermometer,
  MapPin,
  Settings,
  User,
  LogOut,
  Menu,
  X,
  Home,
  Heart,
  Leaf,
  CheckCircle2,
  Calendar as CalendarIcon,
  Cloud as CloudIcon,
  RefreshCw,
  AlertTriangle,
  Lock,
  Star,
  Zap,
  Sprout,
  Scissors,
  Shovel,
  Bell,
  BellOff,
  Search,
  Share2,
  ChevronDown,
  ChevronUp,
  Droplet,
  Eye,
  Gauge,
  Navigation,
  Shield,
} from "lucide-react"
import { cn } from "../lib/utils"
import { useAuth } from "../contexts/AuthContext"
import "./Weather.css"

// Weather condition configurations
const weatherConditions = {
  sunny: { 
    icon: Sun, 
    gradient: "from-yellow-400 via-orange-400 to-red-400",
    bgGradient: "from-yellow-50 via-orange-50 to-red-50",
    textColor: "text-orange-800"
  },
  cloudy: { 
    icon: Cloud, 
    gradient: "from-gray-300 via-gray-400 to-gray-500",
    bgGradient: "from-gray-50 via-gray-100 to-gray-200",
    textColor: "text-gray-700"
  },
  rainy: { 
    icon: CloudRain, 
    gradient: "from-blue-400 via-blue-500 to-blue-600",
    bgGradient: "from-blue-50 via-blue-100 to-blue-200",
    textColor: "text-blue-800"
  },
  snowy: { 
    icon: CloudSnow, 
    gradient: "from-blue-200 via-blue-300 to-blue-400",
    bgGradient: "from-blue-50 via-blue-100 to-blue-150",
    textColor: "text-blue-700"
  },
  stormy: { 
    icon: Zap, 
    gradient: "from-purple-400 via-purple-500 to-purple-600",
    bgGradient: "from-purple-50 via-purple-100 to-purple-200",
    textColor: "text-purple-800"
  }
}

// Gardening tip configurations
const gardeningTips = {
  watering: { icon: Droplet, color: "bg-blue-500", label: "Laistymas" },
  planting: { icon: Sprout, color: "bg-green-500", label: "Sodinimas" },
  harvesting: { icon: Scissors, color: "bg-orange-500", label: "Derlius" },
  protection: { icon: Shield, color: "bg-red-500", label: "Apsauga" },
  maintenance: { icon: Shovel, color: "bg-purple-500", label: "Priežiūra" }
}

// Mock weather data (replace with API calls)
const mockWeatherData = {
  current: {
    location: "Vilnius, Lietuva",
    temperature: 18,
    feelsLike: 16,
    condition: "cloudy",
    description: "Daugiausia debesuota",
    humidity: 65,
    windSpeed: 12,
    uvIndex: 3,
    precipitation: 20,
    pressure: 1013,
    visibility: 10
  },
  forecast: [
    { day: "Šiandien", high: 20, low: 12, condition: "cloudy", precipitation: 30, icon: Cloud },
    { day: "Rytoj", high: 22, low: 14, condition: "sunny", precipitation: 10, icon: Sun },
    { day: "Trečiadienis", high: 19, low: 11, condition: "rainy", precipitation: 80, icon: CloudRain },
    { day: "Ketvirtadienis", high: 17, low: 9, condition: "cloudy", precipitation: 40, icon: Cloud },
    { day: "Penktadienis", high: 21, low: 13, condition: "sunny", precipitation: 5, icon: Sun },
    { day: "Šeštadienis", high: 24, low: 15, condition: "sunny", precipitation: 0, icon: Sun },
    { day: "Sekmadienis", high: 23, low: 14, condition: "cloudy", precipitation: 25, icon: Cloud }
  ],
  alerts: [
    { type: "warning", message: "Vėjo gūsiai iki 25 km/h", icon: Wind },
    { type: "info", message: "Optimali oro drėgmė augalams", icon: Droplet }
  ]
}

// Mock user data
const mockUser = {
  isPremium: false,
  location: "Vilnius",
  units: "celsius"
}

export default function Weather() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const [weatherData, setWeatherData] = useState(mockWeatherData)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [showLocationDialog, setShowLocationDialog] = useState(false)
  const [showSettingsDialog, setShowSettingsDialog] = useState(false)
  const [notificationsEnabled, setNotificationsEnabled] = useState(true)
  const [units, setUnits] = useState<'celsius' | 'fahrenheit'>('celsius')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Navigation items
  const navigationItems = [
    {
      title: "Pagrindinis",
      description: "Pagrindinis skydelis",
      url: '/dashboard',
      icon: Home
    },
    {
      title: "Mano Sodas",
      description: "Jūsų augalų sąrašas",
      url: '/my-plants',
      icon: Heart
    },
    {
      title: "Augalų Katalogas",
      description: "Augalų duomenų bazė",
      url: '/plants',
      icon: Leaf
    },
    {
      title: "Užduotys",
      description: "Priežiūros užduotys",
      url: '/tasks',
      icon: CheckCircle2
    },
    {
      title: "Kalendorius",
      description: "Priežiūros kalendorius",
      url: '/calendar',
      icon: CalendarIcon
    },
    {
      title: "Orai",
      description: "Oro prognozės",
      url: '/weather',
      icon: CloudIcon
    }
  ]

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleNavigation = (url: string) => {
    navigate(url)
    setMobileSidebarOpen(false)
  }

  const getUserInitials = (fullName: string) => {
    return fullName
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const isActiveRoute = (url: string) => {
    return window.location.pathname === url
  }

  const getWeatherCondition = (condition: string) => {
    return weatherConditions[condition as keyof typeof weatherConditions] || weatherConditions.cloudy
  }

  const getGardeningTips = useCallback(() => {
    const tips = []
    const { current } = weatherData

    // Temperature-based tips
    if (current.temperature > 25) {
      tips.push({
        type: 'watering',
        title: 'Intensyvus laistymas',
        description: 'Aukšta temperatūra - laistykite augalus 2-3 kartus per dieną',
        priority: 'high'
      })
    } else if (current.temperature < 5) {
      tips.push({
        type: 'protection',
        title: 'Augalų apsauga',
        description: 'Žema temperatūra - apsaugokite švelnius augalus nuo šalčio',
        priority: 'high'
      })
    }

    // Humidity-based tips
    if (current.humidity < 40) {
      tips.push({
        type: 'watering',
        title: 'Padidinkite drėgmę',
        description: 'Žema oro drėgmė - naudokite purkštukus arba drėgmės generatorių',
        priority: 'medium'
      })
    }

    // Precipitation-based tips
    if (current.precipitation > 70) {
      tips.push({
        type: 'maintenance',
        title: 'Praleiskite laistymą',
        description: 'Laukiama lietaus - šiandien nereikia laistyti augalų',
        priority: 'medium'
      })
    }

    // UV Index-based tips
    if (current.uvIndex > 7) {
      tips.push({
        type: 'protection',
        title: 'Saulės apsauga',
        description: 'Aukštas UV indeksas - apsaugokite augalus nuo tiesioginių saulės spindulių',
        priority: 'medium'
      })
    }

    // Wind-based tips
    if (current.windSpeed > 20) {
      tips.push({
        type: 'protection',
        title: 'Vėjo apsauga',
        description: 'Stiprus vėjas - pritvirtinkite aukštus augalus',
        priority: 'high'
      })
    }

    return tips
  }, [weatherData])

  const refreshWeather = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      // In real app, call: const data = await fetch('/api/v1/weather/current')
      setWeatherData(mockWeatherData)
    } catch (err) {
      setError('Nepavyko atnaujinti oro sąlygų')
    } finally {
      setIsLoading(false)
    }
  }

  const WeatherIcon = ({ condition, size = "w-8 h-8", animated = false }: { 
    condition: string, 
    size?: string, 
    animated?: boolean 
  }) => {
    const config = getWeatherCondition(condition)
    const Icon = config.icon

    return (
      <div className={cn(
        "flex items-center justify-center",
        animated && "animate-pulse"
      )}>
        <Icon className={cn(size, config.textColor)} />
      </div>
    )
  }

  const CurrentWeatherCard = () => {
    const { current } = weatherData
    const config = getWeatherCondition(current.condition)

    return (
      <Card className={cn(
        "relative overflow-hidden border-0 shadow-xl",
        `bg-gradient-to-br ${config.bgGradient}`
      )}>
        <div className="absolute inset-0 bg-gradient-to-br opacity-10 from-white to-transparent" />
        <CardContent className="relative p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{current.location}</h2>
              <p className="text-gray-600">{current.description}</p>
            </div>
            <WeatherIcon condition={current.condition} size="w-16 h-16" animated />
          </div>

          <div className="mb-6">
            <div className="flex items-baseline">
              <span className="text-6xl font-bold text-gray-900">{current.temperature}°</span>
              <span className="text-xl text-gray-600 ml-2">C</span>
            </div>
            <p className="text-gray-600">Jaučiasi kaip {current.feelsLike}°C</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Droplets className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">Drėgmė</p>
                <p className="font-semibold">{current.humidity}%</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Wind className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-sm text-gray-600">Vėjas</p>
                <p className="font-semibold">{current.windSpeed} km/h</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Eye className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-sm text-gray-600">UV indeksas</p>
                <p className="font-semibold">{current.uvIndex}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <CloudRain className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">Krituliai</p>
                <p className="font-semibold">{current.precipitation}%</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  const ForecastCard = ({ day, high, low, condition, precipitation, icon: Icon }: any) => {
    const config = getWeatherCondition(condition)

    return (
      <Card className="min-w-[120px] border-0 shadow-md hover:shadow-lg transition-shadow">
        <CardContent className="p-4 text-center">
          <p className="text-sm font-medium text-gray-600 mb-2">{day}</p>
          <Icon className={cn("w-8 h-8 mx-auto mb-2", config.textColor)} />
          <div className="flex justify-center space-x-2 mb-1">
            <span className="font-semibold text-gray-900">{high}°</span>
            <span className="text-gray-500">{low}°</span>
          </div>
          <p className="text-xs text-gray-500">{precipitation}%</p>
        </CardContent>
      </Card>
    )
  }

  const GardeningTipCard = ({ tip }: { tip: any }) => {
    const config = gardeningTips[tip.type as keyof typeof gardeningTips]

    return (
      <Card className={cn(
        "border-l-4 transition-all hover:shadow-md",
        tip.priority === 'high' ? 'border-red-500' : 'border-blue-500'
      )}>
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <div className={cn("p-2 rounded-lg", config.color)}>
              <config.icon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 mb-1">{tip.title}</h4>
              <p className="text-sm text-gray-600">{tip.description}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  const AlertCard = ({ alert }: { alert: any }) => {
    const Icon = alert.icon

    return (
      <Card className={cn(
        "border-l-4",
        alert.type === 'warning' ? 'border-orange-500 bg-orange-50' : 'border-blue-500 bg-blue-50'
      )}>
        <CardContent className="p-4">
          <div className="flex items-center space-x-3">
            <Icon className={cn(
              "w-5 h-5",
              alert.type === 'warning' ? 'text-orange-600' : 'text-blue-600'
            )} />
            <p className="text-sm text-gray-700">{alert.message}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <div className="flex h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50">
        {/* Desktop Sidebar */}
        <Sidebar className={`hidden lg:flex w-64 flex-col bg-white/80 backdrop-blur-sm border-r border-blue-100 shadow-2xl transition-all duration-300`}>
          {/* Sidebar Header */}
          <SidebarHeader className="bg-gradient-to-br from-blue-50 to-indigo-50 border-b border-blue-100 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white shadow-md">
                <CloudIcon className="h-5 w-5" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold text-blue-800">Kiemo Draugas</span>
                <span className="text-xs text-blue-600">Augalų valdymas</span>
              </div>
            </div>
          </SidebarHeader>
          
          {/* Navigation Content */}
          <SidebarContent className="flex-1 bg-white/80 backdrop-blur-sm px-3 py-4">
            <SidebarNav className="space-y-1">
              {navigationItems.map((item) => {
                const isActive = isActiveRoute(item.url)
                return (
                  <SidebarNavItem
                    key={item.title}
                    active={isActive}
                    as="div"
                    className={cn(
                      "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer",
                      isActive 
                        ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600 shadow-sm' 
                        : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
                    )}
                    onClick={() => handleNavigation(item.url)}
                  >
                    <item.icon className={cn(
                      "h-5 w-5 transition-colors",
                      isActive ? 'text-blue-600' : 'text-gray-500 group-hover:text-blue-600'
                    )} />
                    <div className="flex flex-col">
                      <span>{item.title}</span>
                      <span className="text-xs text-gray-500 font-normal">{item.description}</span>
                    </div>
                  </SidebarNavItem>
                )
              })}
            </SidebarNav>
          </SidebarContent>
          
          {/* Sidebar Footer */}
          <SidebarFooter className="bg-gradient-to-t from-blue-50 to-transparent border-t border-blue-100 px-3 py-4">
            <SidebarNav>
              <SidebarNavItem 
                as="div"
                className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 cursor-pointer"
              >
                <Settings className="h-5 w-5 text-gray-500 group-hover:text-blue-600 transition-colors" />
                <div className="flex flex-col">
                  <span>Nustatymai</span>
                  <span className="text-xs text-gray-500 font-normal">Aplikacijos nustatymai</span>
                </div>
              </SidebarNavItem>
            </SidebarNav>
          </SidebarFooter>
        </Sidebar>

        {/* Mobile Sidebar Overlay */}
        {mobileSidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="fixed inset-0 bg-black/50" onClick={() => setMobileSidebarOpen(false)} />
            <div className="fixed left-0 top-0 h-full w-64 bg-white/80 backdrop-blur-sm shadow-2xl">
              <div className="flex h-full flex-col">
                {/* Mobile Header */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-b border-blue-100 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white shadow-md">
                        <CloudIcon className="h-5 w-5" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-lg font-bold text-blue-800">Kiemo Draugas</span>
                        <span className="text-xs text-blue-600">Augalų valdymas</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-blue-700 hover:bg-blue-100"
                      onClick={() => setMobileSidebarOpen(false)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </div>

                {/* Mobile Navigation */}
                <div className="flex-1 px-3 py-4">
                  <SidebarNav className="space-y-1">
                    {navigationItems.map((item) => {
                      const isActive = isActiveRoute(item.url)
                      return (
                        <SidebarNavItem
                          key={item.title}
                          active={isActive}
                          as="div"
                          className={cn(
                            "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer",
                            isActive 
                              ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600 shadow-sm' 
                              : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
                          )}
                          onClick={() => handleNavigation(item.url)}
                        >
                          <item.icon className={cn(
                            "h-5 w-5 transition-colors",
                            isActive ? 'text-blue-600' : 'text-gray-500 group-hover:text-blue-600'
                          )} />
                          <div className="flex flex-col">
                            <span>{item.title}</span>
                            <span className="text-xs text-gray-500 font-normal">{item.description}</span>
                          </div>
                        </SidebarNavItem>
                      )
                    })}
                  </SidebarNav>
                </div>

                {/* Mobile Footer */}
                <div className="bg-gradient-to-t from-blue-50 to-transparent border-t border-blue-100 px-3 py-4">
                  <SidebarNav>
                    <SidebarNavItem 
                      as="div"
                      className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 cursor-pointer"
                    >
                      <Settings className="h-5 w-5 text-gray-500 group-hover:text-blue-600 transition-colors" />
                      <div className="flex flex-col">
                        <span>Nustatymai</span>
                        <span className="text-xs text-gray-500 font-normal">Aplikacijos nustatymai</span>
                      </div>
                    </SidebarNavItem>
                  </SidebarNav>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="bg-white border-b px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="lg:hidden"
                  onClick={() => setMobileSidebarOpen(true)}
                >
                  <Menu className="h-5 w-5" />
                </Button>
                
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Orai</h1>
                  <p className="text-sm text-gray-500">Oro prognozės ir augalų priežiūros patarimai</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button 
                  onClick={refreshWeather} 
                  disabled={isLoading}
                  variant="outline" 
                  size="sm"
                  className="border-blue-200 text-blue-700 hover:bg-blue-50"
                >
                  <RefreshCw className={cn("h-4 w-4 mr-2", isLoading && "animate-spin")} />
                  Atnaujinti
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowLocationDialog(true)}
                  className="border-blue-200 text-blue-700 hover:bg-blue-50"
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  Vieta
                </Button>

                                <div className="relative dropdown-container" ref={dropdownRef}>
                  <Button
                    variant="ghost"
                    className="relative h-8 w-8 rounded-full hover:bg-blue-50"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <Avatar className="h-8 w-8 border-2 border-blue-200">
                      <AvatarImage src="/placeholder.svg?height=32&width=32" alt="User avatar" />
                      <AvatarFallback className="bg-blue-100 text-blue-700">
                        {user ? getUserInitials(user.full_name) : 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>

                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 user-dropdown py-1">
                      <div className="px-4 py-3 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900">
                          {user ? user.full_name : 'Nežinomas vartotojas'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {user ? user.email : ''}
                        </p>
                      </div>
                      
                      <div className="py-1">
                        <div className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer">
                          <User className="w-4 h-4" />
                          <span>Profilis</span>
                        </div>
                        <div 
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer"
                          onClick={() => {
                            setIsDropdownOpen(false)
                            setShowSettingsDialog(true)
                          }}
                        >
                          <Settings className="w-4 h-4" />
                          <span>Nustatymai</span>
                        </div>
                      </div>
                      
                      <div className="border-t border-gray-200 py-1">
                        <div 
                          className="flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                          onClick={() => {
                            setIsDropdownOpen(false)
                            handleLogout()
                          }}
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Atsijungti</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 overflow-auto p-6">
            {/* Error Display */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2 text-red-600 border-red-300 hover:bg-red-100"
                  onClick={() => setError(null)}
                >
                  Uždaryti
                </Button>
              </div>
            )}

            <div className="space-y-6">
              {/* Current Weather */}
              <CurrentWeatherCard />

              {/* Weather Alerts */}
              {weatherData.alerts.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Oro įspėjimai</h3>
                  <div className="space-y-2">
                    {weatherData.alerts.map((alert, index) => (
                      <AlertCard key={index} alert={alert} />
                    ))}
                  </div>
                </div>
              )}

              {/* 7-Day Forecast */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">7 dienų prognozė</h3>
                  {!mockUser.isPremium && (
                    <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                      <Lock className="w-3 h-3 mr-1" />
                      Premium
                    </Badge>
                  )}
                </div>
                
                <div className="relative">
                  <div className="flex space-x-3 overflow-x-auto pb-2">
                    {weatherData.forecast.map((day, index) => (
                      <ForecastCard key={index} {...day} />
                    ))}
                  </div>
                  
                  {!mockUser.isPremium && (
                    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center rounded-lg">
                      <div className="text-center">
                        <Lock className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600 mb-2">Atnaujinkite į Premium</p>
                        <Button size="sm" className="bg-yellow-500 hover:bg-yellow-600">
                          <Star className="w-4 h-4 mr-2" />
                          Atnaujinti
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Gardening Tips */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Augalų priežiūros patarimai</h3>
                <div className="space-y-3">
                  {getGardeningTips().map((tip, index) => (
                    <GardeningTipCard key={index} tip={tip} />
                  ))}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Location Dialog */}
      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pakeisti vietą</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="location">Miestas</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input 
                  id="location" 
                  placeholder="Įveskite miestą..." 
                  className="pl-10"
                  defaultValue={weatherData.current.location}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setShowLocationDialog(false)} variant="outline">
                Atšaukti
              </Button>
              <Button onClick={() => setShowLocationDialog(false)}>
                Išsaugoti
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Oro nustatymai</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Pranešimai</Label>
                <p className="text-sm text-gray-500">Gaukite oro įspėjimus</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                className={notificationsEnabled ? 'bg-blue-50 border-blue-200' : ''}
              >
                {notificationsEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              </Button>
            </div>
            
            <div>
              <Label>Temperatūros vienetai</Label>
              <div className="flex space-x-2 mt-2">
                <Button
                  variant={units === 'celsius' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setUnits('celsius')}
                >
                  Celsius
                </Button>
                <Button
                  variant={units === 'fahrenheit' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setUnits('fahrenheit')}
                >
                  Fahrenheit
                </Button>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={() => setShowSettingsDialog(false)} variant="outline">
                Atšaukti
              </Button>
              <Button onClick={() => setShowSettingsDialog(false)}>
                Išsaugoti
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
} 